import { siteApiSlice } from "../../app/siteApiSlice";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { DataTablesQueryModel, DataTablesViewModel } from "../../interfaces/dataTables";
import NamedEntity from "../../interfaces/NamedEntity";

export enum SchoolFilter {
	Active = "Active Schools",
	Inactive = "Inactive Schools",
	All = "All Schools",
}

export interface SelectSchoolViewModel {
	schools: SelectSchoolRowViewModel[];
	alerts: Record<number, string>;
}

export interface SelectSchoolRowViewModel {
	id: number;
	name: string;
	active: boolean;
	expirationDate: string;
	hasExpired: boolean;
	numCourses: number;
	numSeats: string;
	isCleverSchool: boolean;
	isClassLinkSchool: boolean;
}

export interface SchoolListInfoViewModel {
	takenSeats: number;
	totalSeats: number;
	alerts: Record<number, string>;
}

export interface SchoolListQueryModel extends DataTablesQueryModel {
	searchString: string;
	selectedActiveSchoolFilter: string;
}

export interface SchoolListViewModel {
	dT_RowId: string;
	id: string;
	name: string;
	active: boolean;
	expirationDate: string;
	hasExpired: boolean;
	districtName: string;
	contact: string;
	phone: string;
	numCourses: number;
	numSeats: string;
	isCleverSchool: boolean;
	isClassLinkSchool: boolean;
}

export interface SchoolSpaViewModel {
	id: number;
	name: string;
	district?: NamedEntity;
	externalID: string;
	address: string;
	address2: string;
	city: string;
	zoneId?: number;
	countryId?: number;
	zipCode: string;
	contactName: string;
	contactEmail: string;
	contactPhone: string;
	referralCode: string;
	active: boolean;
	expirationDateString: string;
	expirationDate: Date;
	hasExpired: boolean;
	totalSeats: number;
	takenSeats: number;
	allowCourseCreation: boolean;
	normativeReportsEnabled: boolean;
	cleverID: string;
	showCleverID: boolean;
	classLinkID: string;
	showClassLinkID: boolean;
	disableUserImages: boolean;
	disableUserImagesByDistrict: boolean;
	alerts: Record<number, string>; // for principal only
	clearDismissedAlerts: boolean; // for principal only
	showRosterSection: boolean;
}

export const emptySchoolViewModel: SchoolSpaViewModel = {
	id: 0,
	name: "",
	district: undefined,
	externalID: "",
	address: "",
	address2: "",
	city: "",
	zoneId: 0,
	countryId: 0,
	zipCode: "",
	contactName: "",
	contactEmail: "",
	contactPhone: "",
	referralCode: "",
	active: false,
	expirationDateString: "",
	expirationDate: new Date(),
	hasExpired: false,
	totalSeats: 0,
	takenSeats: 0,
	allowCourseCreation: false,
	normativeReportsEnabled: false,
	cleverID: "",
	showCleverID: false,
	classLinkID: "",
	showClassLinkID: false,
	disableUserImages: false,
	disableUserImagesByDistrict: false,
	alerts: {},
	clearDismissedAlerts: false,
	showRosterSection: false,
};

export interface SchoolEditModel {
	id: number;
	model: SchoolSpaViewModel;
}

export interface CourseListQueryModel extends DataTablesQueryModel {
	school_id: number;
	search_filter: string;
	active_filter: string;
}

export interface CourseListViewModel {
	courseId: number;
	isClassLinkCourse: boolean;
	isCleverCourse: boolean;
	name: string;
	externalID: string;
	status: string;
	createDate: Date;
	referralCode: string;
	grade: string;
	writerCount: number;
}

export interface SchoolAdminQueryModel extends DataTablesQueryModel {
	schoolId: number;
}

export interface SchoolAdminListViewModel {
	userId: string;
	userName: string;
	fullName: string;
	phoneNumber: string;
	email: string;
	hasImage: boolean;
	initials: string;
}

export interface AddRemoveUserToSchoolViewModel {
	schoolId: number;
	userId: number;
}

export interface SchoolSeatLimitViewModel {
	schoolId: number;
	seatLimit: number;
}

export interface SchoolActivationViewModel {
	active: boolean;
	expirationModel: SchoolExpirationDateViewModel;
}

export interface SchoolExpirationDateViewModel {
	schoolId: number;
	noExpirationDate: boolean;
	schoolExpirationDate: Date;
}

export interface SchoolOutsideSiteViewModel {
	schoolId: number;
	isOnboarding: boolean;
	lastSyncAt: Date;
}

export interface SchoolCleverSettingsViewModel extends SchoolOutsideSiteViewModel {
	cleverId: string;
}

export interface SchoolClassLinkSettingsViewModel extends SchoolOutsideSiteViewModel {
	classLinkId: string;
}

export const schoolsApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["SchoolAdmins", "SchoolCleverSettings", "SchoolClassLinkSettings"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getSchoolPickerOptions: build.query<NamedEntity[], void>({
				query: () => ({
					url: `/School/SchoolPickerOptions`,
				}),
			}),
			getSelectSchoolOptions: build.query<SelectSchoolViewModel, void>({
				query: () => ({
					url: `/SignIn/SelectSchool`,
				}),
				providesTags: ["Alerts"],
			}),
			makeSchoolSelection: build.mutation<NamedEntity | null, number>({
				query: (id) => ({
					url: `/SignIn/MakeSchoolSelection`,
					params: { id },
				}),
				invalidatesTags: ["CurrentSchool"],
			}),
			schoolListInfo: build.query<SchoolListInfoViewModel, void>({
				query: () => ({
					url: `/School/List`,
				}),
				providesTags: ["Schools", "CurrentDistrict", "SeatCount"],
			}),
			schoolList: build.query<DataTablesViewModel<SchoolListViewModel>, SchoolListQueryModel>(
				{
					query: (q) => {
						const {
							iSortCol_0,
							sSortDir_0,
							iDisplayStart,
							iDisplayLength,
							searchString,
							selectedActiveSchoolFilter,
						} = q;
						return {
							url: `/School/ListAjaxHandler`,
							params: {
								iSortCol_0,
								sSortDir_0,
								iDisplayStart,
								iDisplayLength,
								searchString,
								selectedActiveSchoolFilter,
							},
						};
					},
					providesTags: ["Schools", "CurrentDistrict", "SeatCount"],
				}
			),
			getSchoolInfo: build.query<SchoolSpaViewModel, number>({
				query: (id) => ({
					url: `/School/Edit`,
					params: { id },
				}),
				providesTags: ["School", "CurrentSchool", "SeatCount"],
			}),
			addSchool: build.mutation<number, SchoolSpaViewModel>({
				query: (model) => {
					return {
						url: `/School/AddForSpa`,
						method: "POST",
						body: { model },
					};
				},
				invalidatesTags: ["Schools", "School", "Districts"],
			}),
			editSchool: build.mutation<void, SchoolEditModel>({
				query: (q) => {
					const { id, model } = q;
					return {
						url: `/School/EditForSpa`,
						method: "POST",
						body: { id, model },
					};
				},
				invalidatesTags: [
					"Schools",
					"School",
					"CourseInfo",
					"UserInfo",
					"Districts",
					"SeatCount",
				],
			}),
			schoolCoursesList: build.query<
				DataTablesViewModel<CourseListViewModel>,
				CourseListQueryModel
			>({
				query: (q) => {
					const {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						school_id,
						search_filter,
						active_filter,
					} = q;
					return {
						url: `/School/AjaxHandler`,
						params: {
							iSortCol_0,
							sSortDir_0,
							iDisplayStart,
							iDisplayLength,
							school_id,
							search_filter,
							active_filter,
						},
					};
				},
				providesTags: ["School", "SchoolCourses", "CurrentSchool", "SeatCount"],
			}),
			schoolAdminsList: build.query<
				DataTablesViewModel<SchoolAdminListViewModel>,
				SchoolAdminQueryModel
			>({
				query: (q) => {
					const { iSortCol_0, sSortDir_0, iDisplayStart, iDisplayLength, schoolId } = q;
					return {
						url: `/User/SchoolAdminsAjaxHandler`,
						params: {
							iSortCol_0,
							sSortDir_0,
							iDisplayStart,
							iDisplayLength,
							schoolId,
						},
					};
				},
				providesTags: ["School", "SchoolAdmins"],
			}),
			getAvailableSchoolAdmins: build.query<SimpleOption[], number>({
				query: (schoolId) => ({
					url: `/School/GetAvailableSchoolAdminsInDistrict`,
					params: { schoolId },
				}),
				providesTags: ["SchoolAdmins"],
			}),
			addUserToSchool: build.mutation<void, AddRemoveUserToSchoolViewModel>({
				query: (q) => {
					const { schoolId, userId } = q;
					return {
						url: `/User/AddSchool`,
						method: "POST",
						body: { schoolId, userId },
					};
				},
				invalidatesTags: ["SchoolAdmins", "UserInfo", "SeatCount"],
			}),
			removeUserFromSchool: build.mutation<void, AddRemoveUserToSchoolViewModel>({
				query: (q) => {
					const { schoolId, userId } = q;
					return {
						url: `/User/RemoveSchool`,
						method: "POST",
						body: { schoolId, userId },
					};
				},
				invalidatesTags: [
					"SchoolAdmins",
					"UserInfo",
					"UserList",
					"SeatCount",
					"CourseInfo",
				],
			}),
			activateSchool: build.mutation<void, number>({
				query: (id) => {
					return {
						url: `/School/Activate`,
						method: "POST",
						body: { id },
					};
				},
				invalidatesTags: ["Schools", "UserInfo"],
			}),
			deactivateSchool: build.mutation<void, number>({
				query: (id) => {
					return {
						url: `/School/Deactivate`,
						method: "POST",
						body: { id },
					};
				},
				invalidatesTags: ["Schools", "UserInfo"],
			}),
			getExpirationDate: build.query<SchoolExpirationDateViewModel, number>({
				query: (schoolId) => ({
					url: `/School/AdjustExpirationDate`,
					params: { schoolId },
				}),
			}),
			adjustExpirationDate: build.mutation<void, SchoolExpirationDateViewModel>({
				query: (payload) => ({
					url: `/School/AdjustExpirationDate`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Schools"],
			}),
			adjustSeatLimit: build.mutation<void, SchoolSeatLimitViewModel>({
				query: (payload) => ({
					url: `/School/AdjustSeatLimit`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["SeatCount"],
			}),
			allowNormative: build.mutation<void, number>({
				query: (id) => {
					return {
						url: `/School/AllowNormative`,
						method: "POST",
						body: { id },
					};
				},
			}),
			forbidNormative: build.mutation<void, number>({
				query: (id) => {
					return {
						url: `/School/ForbidNormative`,
						method: "POST",
						body: { id },
					};
				},
			}),
			updateUserAvatarSettingForSchool: build.mutation<
				void,
				{ schoolId: number; disabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/School/UpdateAvatarSettingForSchool`,
						method: "POST",
						body: payload,
					};
				},
			}),
			generateReferralCode: build.mutation<string, number>({
				query: (id) => {
					return {
						url: `/School/GenerateReferralCode`,
						method: "POST",
						body: { id },
					};
				},
			}),
			getSchoolCleverSettings: build.query<SchoolCleverSettingsViewModel, number>({
				query: (schoolId) => ({
					url: `/School/EditCleverSettings`,
					method: "GET",
					params: { schoolId },
				}),
				providesTags: ["SchoolCleverSettings"],
			}),
			updateSchoolCleverSettings: build.mutation<void, SchoolCleverSettingsViewModel>({
				query: (payload) => ({
					url: `/School/EditCleverSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Schools", "SchoolCleverSettings", "UserInfo"],
			}),
			getSchoolClassLinkSettings: build.query<SchoolClassLinkSettingsViewModel, number>({
				query: (schoolId) => ({
					url: `/School/EditClassLinkSettings`,
					method: "GET",
					params: { schoolId },
				}),
				providesTags: ["SchoolClassLinkSettings"],
			}),
			updateSchoolClassLinkSettings: build.mutation<void, SchoolClassLinkSettingsViewModel>({
				query: (payload) => ({
					url: `/School/EditClassLinkSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["Schools", "SchoolClassLinkSettings", "UserInfo"],
			}),
		}),
	});

export const {
	useGetSchoolPickerOptionsQuery,
	useGetSelectSchoolOptionsQuery,
	useMakeSchoolSelectionMutation,
	useSchoolListInfoQuery,
	useSchoolListQuery,
	useLazyGetSchoolInfoQuery,
	useAddSchoolMutation,
	useEditSchoolMutation,
	useSchoolCoursesListQuery,
	useSchoolAdminsListQuery,
	useGetAvailableSchoolAdminsQuery,
	useAddUserToSchoolMutation,
	useRemoveUserFromSchoolMutation,
	useActivateSchoolMutation,
	useDeactivateSchoolMutation,
	useGetExpirationDateQuery,
	useAdjustExpirationDateMutation,
	useAdjustSeatLimitMutation,
	useAllowNormativeMutation,
	useForbidNormativeMutation,
	useUpdateUserAvatarSettingForSchoolMutation,
	useGenerateReferralCodeMutation,
	useGetSchoolCleverSettingsQuery,
	useUpdateSchoolCleverSettingsMutation,
	useGetSchoolClassLinkSettingsQuery,
	useUpdateSchoolClassLinkSettingsMutation,
} = schoolsApiSlice;
